import React from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash2, Plus } from "lucide-react";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceList } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { SafeDeleteDialog } from "@/components/kit/SafeDeleteDialog";
import { OfferThumb } from "@/components/offers/OfferThumb";
import { useBulkActions, BulkDeleteOffersDialog } from "@/components/kit/bulk";
import { Button } from "@/components/ui/button";
import { fmtDate, pubId } from "@/lib/format";
import { useTenantColumn } from "@/hooks/useTenantColumn";
import { useTenants } from "@/hooks/useTenants";
import { ProductDrawer } from "@/drawers/ProductDrawer";

const asOpts = (arr) => (arr || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));

export default function Products() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const opts = useOptions();
  const [drawer, setDrawer] = React.useState({ open: false, item: null });
  const [del, setDel] = React.useState({ open: false, item: null });
  const { data, isLoading } = useResourceList("products", {});
  const tcol = useTenantColumn("products");
  const { data: tenantsData } = useTenants();
  const bulk = useBulkActions("products", { noun: "product" });

  const items = data?.items || [];
  const transferOptions = items.map((p) => ({ value: p.id, label: p.name }));

  const openOffers = (e, row) => {
    e.stopPropagation();
    navigate(`/offers?product_id=${row.id}`);
  };

  const columns = [
    { key: "id", label: "ID", mono: true, render: (r) => <span className="text-muted-foreground">{pubId(r)}</span> },
    { key: "name", label: "Name", sortable: true, render: (r) => (
      <div className="flex items-center gap-2.5">
        <OfferThumb image={r.image_url} name={r.name} size="sm" />
        <span className="font-medium text-foreground">{r.name}</span>
      </div>
    ) },
    { key: "vertical", label: "Vertical", sortable: true },
    { key: "active_offers", label: "Active offers", align: "right", sortable: true, sortValue: (r) => Number(r.active_offers || 0), exportValue: (r) => r.active_offers || 0,
      render: (r) => (r.active_offers ? (
        <button data-testid={`product-offers-${r.id}`} onClick={(e) => openOffers(e, r)}
          className="font-mono text-sm text-[#60A5FA] underline-offset-2 hover:underline">{r.active_offers}</button>
      ) : <span className="font-mono text-muted-foreground">0</span>) },
    { key: "created_at", label: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
  ];
  const filters = [
    ...(tcol.show ? [{ key: "tenant_id", label: "Tenant", getValue: (r) => r.tenant_id, options: (tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })) }] : []),
    { key: "vertical", label: "Vertical", getValue: (r) => r.vertical, options: asOpts(opts.verticals) },
  ];
  const rowActions = (row) => [
    { label: "Edit", icon: Pencil, onClick: () => setDrawer({ open: true, item: row }), testId: `edit-product-${row.id}` },
    ...(hasPerm(user, "products.delete") ? [{ label: "Delete", icon: Trash2, danger: true, onClick: () => setDel({ open: true, item: row }), testId: `delete-product-${row.id}` }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Products" subtitle="Brands & projects — linked to advertisers only via offers." />
      <DataTablePro testId="products-table" columns={tcol.inject(columns)} rows={items} loading={isLoading}
        filters={filters} searchKeys={["name", "vertical"]} searchPlaceholder="Search products..." exportName="products"
        onRowClick={(r) => setDrawer({ open: true, item: r })}
        rowActions={hasPerm(user, "products.edit") ? rowActions : null} emptyText="No products found."
        selectable={hasPerm(user, "products.delete")} onBulkAction={bulk.handleBulk} bulkActions={["delete"]}
        toolbarRight={hasPerm(user, "products.create") && (
          <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="add-product-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add Product</Button>
        )} />
      <ProductDrawer open={drawer.open} item={drawer.item} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} />
      <SafeDeleteDialog open={del.open} item={del.item} resource="products" transferOptions={transferOptions}
        onOpenChange={(o) => setDel((d) => ({ ...d, open: o }))} />
      <BulkDeleteOffersDialog pending={bulk.pending} busy={bulk.busy} noun="product"
        onCancel={() => bulk.setPending(null)} onConfirm={bulk.confirmDisableDelete} />
    </div>
  );
}
